/**
 * Document write-back (RPA status two) cell for medical PA worklist tables.
 *
 * Renders upload status as a colored document icon with tooltip, and either
 * propagates a click to the parent or opens the image viewer modal.
 */
import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { STATUS_COLORS } from "../../../../constants/statusColors";
import { DocWriteBackStatus } from "../../../../enums/evBvWriteBackStatus";
import { MedicalPaOrdersAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import { ModalId } from "../../../../enums/modalId";
import { setOpenedModalId } from "../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../redux/store/store";
import DocumentUploadIconWithBgColor from "../../../../svg/documentUploadIconWithBgColor";
import ToolTipUsingPortal from "../../../toolTipUsingPortal/toolTipUsingPortal";

/** Props for the document write-back (RPA status two) table cell. */
interface RpaStatusTwoCellProps {
  /** Row payload driving icon color, tooltip, and click behavior. */
  value: {
    /** Current document write-back status. */
    status: string;
    /** Screenshot/file paths opened in the image viewer when not propagating. */
    files?: string[];
    /** When true, click calls the parent callback instead of opening a modal. */
    shouldPropagate?: boolean;
    /** Order id passed to the parent propagate callback. */
    id?: string;
    /** Master auth status used to decide if not-initiated rows are clickable. */
    masterAuthStatus?: string;
  };
  /** Parent handler invoked when ``value.shouldPropagate`` is true. */
  onPropagateEventOnClickInRpaStatusTwoCell?: (id: string) => void;
}

const SHIFT_MASTER_QUEUE_STATUSES_FOR_MODAL = [
  MedicalPaOrdersAuthStatus.Pending,
  MedicalPaOrdersAuthStatus.AuthRequired,
  MedicalPaOrdersAuthStatus.Query,
  MedicalPaOrdersAuthStatus.Hold,
] as string[];

/**
 * Table cell for document write-back / upload status (RPA status two).
 *
 * Shows a colored document-upload icon with a status tooltip. Click propagates
 * to the parent or opens the image viewer when the row is eligible.
 *
 * @param value - Row status, optional file paths, and propagation metadata
 * @param onPropagateEventOnClickInRpaStatusTwoCell - Parent handler when
 *   ``shouldPropagate`` is true
 */
export const RpaStatusTwoCell: React.FC<RpaStatusTwoCellProps> = ({
  value,
  onPropagateEventOnClickInRpaStatusTwoCell,
}) => {
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const commentModalRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();

  const getColorBasedOnStatus = () => {
    switch (value.status) {
      case DocWriteBackStatus.SUCCESS:
        return STATUS_COLORS.SUCCESS.stroke;
      case DocWriteBackStatus.ERROR:
        return STATUS_COLORS.ERROR.stroke;
      case DocWriteBackStatus.IN_PROGRESS:
      case DocWriteBackStatus.QUEUED:
        return STATUS_COLORS.IN_PROGRESS.stroke;
      default:
        return STATUS_COLORS.DEFAULT.stroke;
    }
  };

  const getBackgroundColorBasedOnStatus = () => {
    switch (value.status) {
      case DocWriteBackStatus.SUCCESS:
        return STATUS_COLORS.SUCCESS.background;
      case DocWriteBackStatus.ERROR:
        return STATUS_COLORS.ERROR.background;
      case DocWriteBackStatus.IN_PROGRESS:
      case DocWriteBackStatus.QUEUED:
        return STATUS_COLORS.IN_PROGRESS.background;
      default:
        return STATUS_COLORS.DEFAULT.background;
    }
  };

  const getStatusTextTwo = () => {
    switch (value.status) {
      case DocWriteBackStatus.SUCCESS:
        return "Doc(s) are added";
      case DocWriteBackStatus.ERROR:
        return "Failed to add doc(s)";
      case DocWriteBackStatus.IN_PROGRESS:
      case DocWriteBackStatus.QUEUED:
        return "Doc(s) are being added";
      case DocWriteBackStatus.MANUAL_REQUIRED:
        return "Manual action required";
      default:
        return "Doc(s) not initiated";
    }
  };

  const shouldPropagateEventOrOpenModal = () => {
    if (value.shouldPropagate === true) {
      onPropagateEventOnClickInRpaStatusTwoCell?.(value.id ?? "");
    } else {
      dispatch(
        setOpenedModalId({
          id: ModalId.IMAGE_VIEWER_MODAL,
          metaData: { filePaths: value.files },
        }),
      );
    }
  };

  return (
    <div
      className="table-cell-rpa-status ml-auto h-fit w-fit"
      onMouseEnter={() => setIsCommentModalOpen(true)}
      onMouseLeave={() => setIsCommentModalOpen(false)}
      ref={commentModalRef}
      onClick={(e) => {
        e.stopPropagation();
        if (
          SHIFT_MASTER_QUEUE_STATUSES_FOR_MODAL.includes(
            value.masterAuthStatus ?? "",
          ) &&
          value.status === DocWriteBackStatus.NOT_INITIATED
        ) {
          shouldPropagateEventOrOpenModal();
        } else if (value.status === DocWriteBackStatus.SUCCESS) {
          shouldPropagateEventOrOpenModal();
        }
      }}
    >
      <DocumentUploadIconWithBgColor
        fillColor={getBackgroundColorBasedOnStatus()}
        strokeColor={getColorBasedOnStatus()}
        height={"24px"}
        width={"24px"}
      />

      <ToolTipUsingPortal
        children={getStatusTextTwo()}
        placement={"left"}
        showTooltip={isCommentModalOpen}
        parentRef={commentModalRef}
      />
    </div>
  );
};

export default RpaStatusTwoCell;
